import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  FileText, 
  ExternalLink, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  Folder,
  Link,
  Plus
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const SharePointIntegrationSettings = () => {
  const [sharePointUrl, setSharePointUrl] = useState('');
  const [documentTemplates, setDocumentTemplates] = useState([
    {
      id: 1,
      name: 'Design Specifications',
      url: 'https://yourcompany.sharepoint.com/sites/ProductDevelopment/Shared%20Documents/Design%20Specs/',
      description: 'Product design documentation and specifications'
    },
    {
      id: 2,
      name: 'Technical Drawings',
      url: 'https://yourcompany.sharepoint.com/sites/ProductDevelopment/Shared%20Documents/CAD%20Files/',
      description: 'CAD files and technical drawings'
    },
    {
      id: 3,
      name: 'Project Files',
      url: 'https://yourcompany.sharepoint.com/sites/ProjectManagement/Shared%20Documents/',
      description: 'General project documentation and files'
    }
  ]);
  const [newTemplate, setNewTemplate] = useState({ name: '', url: '', description: '' });
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const { toast } = useToast();

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "URL copied to clipboard",
    });
  };

  const addTemplate = () => {
    if (!newTemplate.name || !newTemplate.url) {
      toast({
        title: "Error",
        description: "Please fill in template name and URL",
        variant: "destructive",
      });
      return;
    }

    const template = {
      id: Date.now(),
      ...newTemplate
    };
    
    setDocumentTemplates([...documentTemplates, template]);
    setNewTemplate({ name: '', url: '', description: '' });
    setShowAddTemplate(false);
    
    toast({
      title: "Success",
      description: "SharePoint template added successfully",
    });
  };

  const removeTemplate = (id) => {
    setDocumentTemplates(documentTemplates.filter(t => t.id !== id));
    toast({
      title: "Success",
      description: "Template removed successfully",
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <h3 className="text-lg font-semibold">SharePoint Integration</h3>
          </div>
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <Folder className="w-3 h-3 mr-1" />
            Document Links
          </Badge>
        </div>
        <p className="text-sm text-slate-600">
          Configure SharePoint links and document templates for easy access in product workflows
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-800">How SharePoint Integration Works</h4>
              <p className="text-sm text-blue-700 mt-1">
                SharePoint integration in ExledOS works through document links in product stage progress. 
                You can add SharePoint folder links, document URLs, and file paths directly in the stage progress modal.
              </p>
              <div className="mt-3 text-sm text-blue-700">
                <p><strong>Current Features:</strong></p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Add SharePoint folder links in stage progress notes</li>
                  <li>Quick templates for common document types</li>
                  <li>Direct links to SharePoint sites and documents</li>
                  <li>Support for local network paths and cloud storage</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* SharePoint Site URL */}
        <div className="space-y-3">
          <h4 className="font-medium text-slate-900">Main SharePoint Site</h4>
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="https://yourcompany.sharepoint.com/sites/..."
                value={sharePointUrl}
                onChange={(e) => setSharePointUrl(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(sharePointUrl)}
              disabled={!sharePointUrl}
            >
              <Copy className="w-4 h-4" />
            </Button>
            {sharePointUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(sharePointUrl, '_blank')}
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Enter your main SharePoint site URL for quick access
          </p>
        </div>

        {/* Document Templates */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-900">Document Link Templates</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddTemplate(!showAddTemplate)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Template
            </Button>
          </div>

          {/* Add Template Form */}
          {showAddTemplate && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Template Name</Label>
                  <Input
                    placeholder="Design Specifications"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-sm">SharePoint URL</Label>
                  <Input
                    placeholder="https://sharepoint.com/..."
                    value={newTemplate.url}
                    onChange={(e) => setNewTemplate({...newTemplate, url: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Description (optional)</Label>
                <Textarea
                  placeholder="Description of what this link contains..."
                  value={newTemplate.description}
                  onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})}
                  className="h-20"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={addTemplate}>
                  Add Template
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => {
                    setShowAddTemplate(false);
                    setNewTemplate({ name: '', url: '', description: '' });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Templates List */}
          <div className="space-y-3">
            {documentTemplates.map((template) => (
              <div
                key={template.id}
                className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Folder className="w-4 h-4 text-blue-600" />
                      <h5 className="font-medium text-slate-900">{template.name}</h5>
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{template.description}</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded font-mono text-slate-700 flex-1 truncate">
                        {template.url}
                      </code>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(template.url)}
                      className="h-8 w-8 p-0"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(template.url, '_blank')}
                      className="h-8 w-8 p-0"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTemplate(template.id)}
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    >
                      ×
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800">How to Use SharePoint Links</h4>
              <div className="text-sm text-green-700 mt-1 space-y-2">
                <p>1. <strong>In Stage Progress:</strong> When updating product stages, you can add document links in the notes section</p>
                <p>2. <strong>Quick Templates:</strong> Use the "SharePoint Folder Template" button in stage progress to quickly add pre-configured links</p>
                <p>3. <strong>Direct Access:</strong> All added links are clickable and open in new tabs for easy access</p>
                <p>4. <strong>Organization:</strong> Links are preserved in the product's stage progress history for future reference</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SharePointIntegrationSettings;